<script lang="tsx" setup>
import { computed } from "vue"
import { useRoute } from "vue-router"

import { getPage } from "@/composables/get-page"
import { setPage } from "@/composables/set-page"
import { getPageQuery } from "@/composables/use-page"
import {
  programmePageQuery,
  KirbyProgrammePageData,
  KirbyGroupProjectData,
} from "@/queries/programme"
import { cleanHTML } from "@/utils/clean"

import Main from "@/components/Wrappers/Main.vue"
import PageSection from "@/components/Layout/PageSection.vue"
import InfoPanel from "@/components/Layout/InfoPanel.vue"
import TitleBlock from "@/components/Layout/TitleBlock.vue"
import StudentHeader from "@/components/StudentPage/StudentHeader.vue"
import ImageGallery from "@/components/Media/ImageGallery.vue"
import ReadMoreText from "@/components/Text/ReadMoreText.vue"
import ResponsiveBodyText from "@/components/Text/ResponsiveBodyText.vue"
import ProjectGrid from "@/components/Projects/ProjectGrid.vue"
import PageSpacer from "@/components/Layout/PageSpacer.vue"
import TextLabel from "@/components/Text/TextLabel.vue"
import { KirbyStudentPreviewData } from "@/queries/student"

const route = useRoute()
const query = getPageQuery(route.path, programmePageQuery)
const page = await getPage<KirbyProgrammePageData>(query)
setPage(page)

const projects = computed(() =>
  [...page.students, ...page.groupProjects].filter(s => s.thumbnail),
)

const studentProjects = page.students

const getStudentData = (student: KirbyStudentPreviewData) => {
  const studentProjectData = studentProjects
    .filter(s => s.thumbnail)
    .find(s => s.uri === student.uri)
  const groupProjectData = page.groupProjects
    .filter(gp => gp.thumbnail)
    .find(gp => gp.students.some(s => s.uri === student.uri))
  if (studentProjectData) return studentProjectData
  if (groupProjectData) {
    const groupStudentData = groupProjectData.students.find(
      s => s.uri === student.uri,
    )
    if (groupStudentData) {
      return {
        ...groupStudentData,
        thumbnail: groupProjectData.thumbnail,
        uri: groupProjectData.uri,
      }
    }
  }
}

const studentsHtml = computed(() => {
  const names: string[] = []

  page.students.forEach(student => {
    const data = getStudentData(student)
    if (data && data.thumbnail) {
      names.push(`<a class="link" href="/${data.uri}">${data.name}</a>`)
    } else {
      names.push(`<span class="opacity-50">${student.name}</span>`)
    }
  })
  // const names = page.students.map(student =>
  //   student.thumbnail
  //     ? `<a class="link" href="/${student.uri}">${student.name}</a>`
  //     : `<span class="opacity-50">${student.name}</span>`,
  // )
  return names.length ? `${names.join(", ")}.` : ""
})
</script>

<template>
  <Main>
    <ImageGallery v-if="page.images.length" :images="page.images" />

    <PageSpacer v-if="!page.images.length" class="border-b" />
    <PageSection>
      <InfoPanel>
        <template #header>
          <TextLabel>Programme:</TextLabel>
          <TitleBlock :name="page.title" :subtitle="page.blurb" />
        </template>

        <template #meta>
          <div v-if="page.details">
            <TextLabel>Tutors:</TextLabel>
            <div class="body-text" v-html="cleanHTML(page.details)" />
          </div>

          <div class="pt-8">
            <TextLabel v-if="page.students.length">Students:</TextLabel>
            <div class="inline" v-html="studentsHtml" />
          </div>
        </template>

        <template #body>
          <TextLabel>Programme text:</TextLabel>
          <ResponsiveBodyText :html="page.review" />
        </template>
      </InfoPanel>
    </PageSection>

    <PageSection v-if="projects.length" border class="mt-12">
      <ProjectGrid :students="projects"> Projects </ProjectGrid>
    </PageSection>
  </Main>
</template>
